import React, { useEffect, useState } from "react";
import {
  GraduationCap,
  Building2,
  Calendar,
  X,
  Eye,
  Trash2,
  AlertCircle,
  Award,FileText,
} from "lucide-react";
import { FooterComponents } from "../FooterComponent";
import SidebarComponent from "../SidbarComponent";
import TopBarComponent from "../TopBarComponent";
import axios from "axios";
import { getAuthHeaders } from "../../utils/auth";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";

const initialState = {
  programName: "",
  universityName: "",
  studyYears: "",
  startDate: "",
  endDate: "",
  requirementInput: "",
};

const initialErrors = {
  programName: "",
  universityName: "",
  studyYears: "",
  startDate: "",
  endDate: "",
  requirementInput: "",
};



const AddEducationProgram = ({ onSubmit, onCancel }) => {
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState(initialErrors);
  const [showPreview, setShowPreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const [scholarshipId, setScholarshipId] = useState("");

    const [requirements, setRequirements] = useState([]);

  const { id } = useParams(); // Get the scholarship ID from URL params

  const validateForm = () => {
    const newErrors = { ...initialErrors };
    let isValid = true;

    // Program Name validation
    if (!form.programName.trim()) {
      newErrors.programName = "Program name is required";
      isValid = false;
    } else if (form.programName.length < 3) {
      newErrors.programName = "Program name must be at least 3 characters";
      isValid = false;
    }

    // University Name validation
    if (!form.universityName.trim()) {
      newErrors.universityName = "University name is required";
      isValid = false;
    } else if (form.universityName.length < 3) {
      newErrors.universityName =
        "University name must be at least 3 characters";
      isValid = false;
    }

    // Study Years validation
    if (!form.studyYears) {
      newErrors.studyYears = "Study years is required";
      isValid = false;
    } else if (isNaN(form.studyYears) || parseInt(form.studyYears) < 1) {
      newErrors.studyYears = "Study years must be a positive number";
      isValid = false;
    }

    // Start Date validation
    if (!form.startDate) {
      newErrors.startDate = "Start date is required";
      isValid = false;
    }

    // End Date validation
    if (!form.endDate) {
      newErrors.endDate = "End date is required";
      isValid = false;
    } else if (
      form.startDate &&
      new Date(form.endDate) <= new Date(form.startDate)
    ) {
      newErrors.endDate = "End date must be after start date";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    let updatedForm = {
      ...form,
      [name]: value,
    };

    // Auto-calculate end date when start date or study years change
    if (name === "startDate" || name === "studyYears") {
      const startDate = name === "startDate" ? value : form.startDate;
      const studyYears = name === "studyYears" ? value : form.studyYears;
      
      if (startDate && studyYears && parseInt(studyYears) > 0) {
        const start = new Date(startDate);
        const endDate = new Date(start);
        endDate.setFullYear(start.getFullYear() + parseInt(studyYears));
        
        updatedForm.endDate = endDate.toISOString().split('T')[0];
      }
    }
    
    setForm(updatedForm);
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    // Prevent default form submission
    e.preventDefault();
    if (validateForm()) {
      try {
        setIsSubmitting(true);
        setSubmitError("");

        // Transform requirements array to documents array format
        const requestBody = {
          programName: form.programName,
          universityName: form.universityName,
          studyYears: parseInt(form.studyYears),
          startDate: form.startDate,
          endDate: form.endDate,
          documents: requirements.map((req) => ({
            documentName: req,
          })),
        };

        const response = await axios.post(
          process.env.REACT_APP_BASE_URL +
            `hec/education-program/save?scholarship=${scholarshipId}`,
          requestBody,
          {
            headers: {
              "Content-Type": "application/json",
              ...getAuthHeaders(),
            },
          }
        );

        if (response.status === 200 || response.status === 201) {
          // Reset form and show success message
          setForm(initialState);
          setErrors(initialErrors);
          setRequirements([]);

          // Navigate back to the scholarship list or show success message
          //navigate("/scholarship-list");

          // A toast notification here using a library like react-toastify
          toast.success("Education Program created successfully!", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
          });
        }
      } catch (error) {
        console.error("Error saving education program:", error);
        setSubmitError(
          error.response?.data?.message ||
            "Failed to save education program. Please try again."
        );
        toast.error("Failed to create education program. Please try again.", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleClear = () => {
    setForm(initialState);
    setErrors(initialErrors);
    setRequirements([]);
  };

  const handlePreview = () => {
    if (validateForm()) {
      setShowPreview(true);
    }
  };

  const handleClosePreview = () => {
    setShowPreview(false);
  };

  const renderError = (error) => {
    if (!error) return null;
    return (
      <div className="flex items-center mt-1 text-red-600 text-sm">
        <AlertCircle className="w-4 h-4 mr-1" />
        <span>{error}</span>
      </div>
    );
  };

  useEffect(() => {
    // Check if scholarship ID is provided in the URL
    if (id) {
      setScholarshipId(id);
    } else {
      // If no ID, redirect or handle accordingly
      toast.error("No scholarship ID provided.", {
        position: "top-right",
        autoClose: 3000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
      //navigate("/scholarship-list");
    }
  }, [id, navigate]);

  return (
    <div className="bg-gray-50">
      <TopBarComponent />
      <aside
        id="logo-sidebar"
        className="app-legacy-aside transition-transform -translate-x-full sm:translate-x-0 bg-white border-r border-gray-200 dark:bg-gray-800 dark:border-gray-700"
        aria-label="Sidebar"
      >
        <SidebarComponent />
      </aside>
      <div className="p-4 sm:ml-64">
        <div className="p-4 border-2 border-white border-b rounded-lg dark:border-gray-700">
          <div className="min-h-screen bg-gray-50">
            <div className="text-center mt-8">
              <div className="w-16 h-16 bg-gradient-to-r from-cyan-800 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <GraduationCap className="w-8 h-8 text-white" />
              </div>
              <h1 className="text-xl font-bold  mb-2">Education Programs</h1>
            </div>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="bg-white rounded-lg shadow-sm border border-cyan-200 p-6 max-w-2xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-semibold text-cyan-900">
                      Add Education Program
                    </h2>
                    <p className="mt-1 text-sm text-gray-600">
                      Fill in the details of the education program you want to
                      add. This information will be used to create a new program
                      entry in the system. Make sure to provide accurate dates
                      and program details.
                    </p>
                  </div>
                </div>

                {submitError && (
                  <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center text-red-600">
                      <AlertCircle className="w-5 h-5 mr-2" />
                      <span>{submitError}</span>
                    </div>
                  </div>
                )}

                <form className="space-y-4">
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium mb-1 text-cyan-900">
                        Program Name
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <GraduationCap className="w-5 h-5 text-cyan-500" />
                        </div>
                        <input
                          type="text"
                          name="programName"
                          value={form.programName}
                          onChange={handleChange}
                          required
                          className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-colors ${
                            errors.programName
                              ? "border-red-500"
                              : "border-cyan-200"
                          }`}
                          placeholder="Enter program name"
                        />
                      </div>
                      {renderError(errors.programName)}
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1 text-cyan-900">
                        University Name
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Building2 className="w-5 h-5 text-cyan-500" />
                        </div>
                        <input
                          type="text"
                          name="universityName"
                          value={form.universityName}
                          onChange={handleChange}
                          required
                          className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-colors ${
                            errors.universityName
                              ? "border-red-500"
                              : "border-cyan-200"
                          }`}
                          placeholder="Enter university name"
                        />
                      </div>
                      {renderError(errors.universityName)}
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-1 text-cyan-900">
                        Study Duration
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <GraduationCap className="w-5 h-5 text-cyan-500" />
                        </div>
                        <input
                          type="number"
                          name="studyYears"
                          value={form.studyYears}
                          onChange={handleChange}
                          min="1"
                          required
                          className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-colors ${
                            errors.studyYears
                              ? "border-red-500"
                              : "border-cyan-200"
                          }`}
                          placeholder="Enter number of years"
                        />
                      </div>
                      {renderError(errors.studyYears)}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-1 text-cyan-900">
                          Start Date
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Calendar className="w-5 h-5 text-cyan-500" />
                          </div>
                          <input
                            type="date"
                            name="startDate"
                            value={form.startDate}
                            onChange={handleChange}
                            required
                            className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-colors ${
                              errors.startDate
                                ? "border-red-500"
                                : "border-cyan-200"
                            }`}
                          />
                        </div>
                        {renderError(errors.startDate)}
                      </div>

                      <div>
                        <label className="block text-sm font-medium mb-1 text-cyan-900">
                          End Date
                        </label>
                        <div className="relative">
                          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                            <Calendar className="w-5 h-5 text-cyan-500" />
                          </div>
                          <input
                            type="date"
                            name="endDate"
                            value={form.endDate}
                            onChange={handleChange}
                            required
                            className={`w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent transition-colors ${
                              form.startDate && form.studyYears
                                ? "bg-gray-100 text-gray-600 cursor-not-allowed border-gray-300"
                                : errors.endDate
                                ? "border-red-500"
                                : "border-cyan-200"
                            }`}
                          />
                        </div>
                      </div>
                    </div>
                     {/* Requirements Input */}
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-cyan-700 mb-2">
                        <div className="flex items-center space-x-2">
                          <FileText className="w-4 h-4 text-cyan-400" />
                          <span>Required Documents</span>
                        </div>
                      </label>
                      
                      <div className="flex gap-2 mb-2">
                        <input
                          type="text"
                          value={form.requirementInput || ""}
                          onChange={(e) => {
                            const value = e.target.value;
                            setForm((prev) => ({
                              ...prev,
                              requirementInput: value,
                            }));
                          }}
                          className="flex-1 px-4 py-2 border border-cyan-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                          placeholder="Enter a document name and press Add (e.g., Transcript)"
                        />
                        <button
                          type="button"
                          className="px-4 py-2 bg-cyan-700 text-white rounded-lg hover:bg-cyan-800 transition"
                          onClick={() => {
                            if (
                              form.requirementInput &&
                              form.requirementInput.trim().length > 0
                            ) {
                              setRequirements((prev) => [
                                ...prev,
                                form.requirementInput.trim(),
                              ]);
                              setForm((prev) => ({
                                ...prev,
                                requirementInput: "",
                              }));
                            }
                          }}
                        >
                          Add
                        </button>
                      </div>
                      {/* Display requirements */}
                      {requirements.length > 0 && (
                        <div className="bg-cyan-50 p-4 rounded-lg border border-cyan-200">
                          <p className="text-sm font-medium text-cyan-700 mb-2">
                            Added Documents ({requirements.length}):
                          </p>
                          <ul className="space-y-2">
                            {requirements.map((req, idx) => (
                              <li
                                key={idx}
                                className="flex items-center justify-between bg-white p-2 rounded border border-cyan-100"
                              >
                                <span className="text-cyan-900">{req}</span>
                                <button
                                  type="button"
                                  className="ml-2 text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50 transition"
                                  onClick={() =>
                                    setRequirements((prev) =>
                                      prev.filter((_, i) => i !== idx)
                                    )
                                  }
                                  title="Remove"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    {/* Requirements Preview */}
                    {requirements.length > 0 && (
                      <div className="mt-6 bg-cyan-50 p-4 rounded-lg border border-cyan-200">
                        <span className="font-medium text-cyan-700 text-sm">
                          Required Documents Preview ({requirements.length}):
                        </span>
                        <ul className="list-disc pl-6 mt-2 space-y-1 text-cyan-900">
                          {requirements.map((req, idx) => (
                            <li key={idx}>{req}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={handleClear}
                      className="px-4 py-2 border border-cyan-200 rounded-lg text-sm font-medium text-cyan-900 transition-colors flex items-center space-x-2 hover:bg-cyan-50"
                      disabled={isSubmitting}
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Clear</span>
                    </button>
                    <button
                      type="button"
                      onClick={handlePreview}
                      className="px-4 py-2 border border-cyan-200 rounded-lg text-sm font-medium text-cyan-900 transition-colors flex items-center space-x-2 hover:bg-cyan-50"
                      disabled={isSubmitting}
                    >
                      <Eye className="w-4 h-4" />
                      <span>Preview</span>
                    </button>
                    {onCancel && (
                      <button
                        type="button"
                        onClick={onCancel}
                        className="px-4 py-2 border border-cyan-200 rounded-lg text-sm font-medium text-cyan-900 transition-colors hover:bg-cyan-50"
                        disabled={isSubmitting}
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      type="submit"
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className={`px-4 py-2 bg-cyan-800 text-white rounded-lg text-sm font-medium hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-colors ${
                        isSubmitting ? "opacity-50 cursor-not-allowed" : ""
                      }`}
                    >
                      {isSubmitting ? "Saving..." : "Save Program"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        <div>
          <FooterComponents />
        </div>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-2xl w-full mx-4">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-cyan-900">
                Program Preview
              </h3>
              <button
                onClick={handleClosePreview}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-cyan-500" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium mb-1 text-cyan-900">
                  Program Name
                </h4>
                <p className="text-gray-600">
                  {form.programName || "Not specified"}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium mb-1 text-cyan-900">
                  University Name
                </h4>
                <p className="text-gray-600">
                  {form.universityName || "Not specified"}
                </p>
              </div>
              <div>
                <h4 className="text-sm font-medium mb-1 text-cyan-900">
                  Study Years
                </h4>
                <p className="text-gray-600">
                  {form.studyYears || "Not specified"}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium mb-1 text-cyan-900">
                    Start Date
                  </h4>
                  <p className="text-gray-600">
                    {form.startDate || "Not specified"}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-1 text-cyan-900">
                    End Date
                  </h4>
                  <p className="text-gray-600">
                    {form.endDate || "Not specified"}
                  </p>
                </div>
              </div>
              {requirements.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium mb-2 text-cyan-900">
                    Required Documents ({requirements.length})
                  </h4>
                  <ul className="list-disc pl-6 space-y-1 text-gray-600">
                    {requirements.map((req, idx) => (
                      <li key={idx}>{req}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            <div className="mt-6 flex justify-end">
              <button
                onClick={handleClosePreview}
                className="px-4 py-2 bg-cyan-800 text-white rounded-lg text-sm font-medium hover:bg-cyan-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 transition-colors"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddEducationProgram;
